import React, { useState, useEffect } from 'react';
import styles from './Referrals.module.css';

// Animated counter for displaying stats
const AnimatedCounter = ({ value, suffix = '', decimals = 0 }) => {
  const [displayValue, setDisplayValue] = useState('0');
  
  useEffect(() => {
    const parsedValue = parseFloat(value);
    const duration = 1000; // Animation duration in ms
    const frameDuration = 1000/30; // 30fps
    const totalFrames = Math.round(duration / frameDuration);
    
    let currentFrame = 0;
    const initialValue = parseFloat(displayValue);
    const increment = (parsedValue - initialValue) / totalFrames;
    
    if (Math.abs(parsedValue - initialValue) < 0.0001) {
      setDisplayValue(parsedValue.toFixed(decimals));
      return;
    }
    
    const counter = setInterval(() => {
      currentFrame++;
      const progress = currentFrame / totalFrames;
      const currentValue = initialValue + (increment * currentFrame);
      
      setDisplayValue(currentValue.toFixed(decimals));
      
      if (progress === 1) {
        clearInterval(counter);
      }
    }, frameDuration);
    
    return () => clearInterval(counter);
  }, [value, decimals]);
  
  return (
    <span className={styles.animatedValue}>
      {displayValue}{suffix && <span className={styles.valueSuffix}>{suffix}</span>}
    </span>
  );
};

const Referrals = ({ web3, accounts, stakingContract }) => {
  const [referralCode, setReferralCode] = useState('');
  const [referralEarnings, setReferralEarnings] = useState('0');
  const [referralLink, setReferralLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [copySuccess, setCopySuccess] = useState(false);
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    const init = async () => {
      if (web3 && accounts && accounts[0]) {
        setIsLoading(true);
        
        // Generate a user-specific referral code based on their address if they don't have one
        const userAddress = accounts[0];
        const existingCode = localStorage.getItem(`referralCode_${userAddress}`);
        
        if (existingCode) {
          setReferralCode(existingCode);
          
          // Set the referral link with the existing code
          const baseUrl = window.location.origin;
          setReferralLink(`${baseUrl}/?ref=${existingCode}`);
        }
        
        // Get referral earnings from localStorage
        const earnings = localStorage.getItem(`referralEarnings_${userAddress}`) || '0';
        setReferralEarnings(earnings);
        
        // Get referrals from localStorage
        const savedReferrals = JSON.parse(localStorage.getItem(`referrals_${userAddress}`) || '[]');
        setReferrals(savedReferrals);
        
        setIsLoading(false);
      }
    };
    
    init();
  }, [web3, accounts]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
  };

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopySuccess(true);
          showNotification('Referral link copied to clipboard!', 'success');
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
          showNotification('Failed to copy referral link', 'error');
        });
    } catch (error) {
      console.error('Clipboard API not available:', error);
      showNotification('Clipboard functionality not available in your browser', 'error');
    }
  };

  const generateReferralCode = async () => {
    setIsLoading(true);
    try {
      const userAddress = accounts[0];
      
      // Generate a random code with letters and numbers, 8 characters long
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let newCode = '';
      for (let i = 0; i < 8; i++) {
        newCode += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      
      // Save to localStorage
      localStorage.setItem(`referralCode_${userAddress}`, newCode);
      
      // Update state
      setReferralCode(newCode);
      
      // Set the referral link with the new code
      const baseUrl = window.location.origin;
      setReferralLink(`${baseUrl}/?ref=${newCode}`);
      
      showNotification('Referral code generated successfully!', 'success');
    } catch (error) {
      console.error('Error generating referral code:', error);
      showNotification('Failed to generate referral code', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdrawReferralRewards = async () => {
    if (parseFloat(referralEarnings) <= 0) {
      showNotification('No earnings available to withdraw', 'error');
      return;
    }
    
    setIsLoading(true);
    try {
      const userAddress = accounts[0];
      
      // In a real implementation, this would transfer tokens to the user
      // For demo purposes, we'll just reset the earnings in localStorage
      
      // Reset earnings
      localStorage.setItem(`referralEarnings_${userAddress}`, '0');
      
      // Update state
      setReferralEarnings('0');
      
      showNotification('Earnings successfully withdrawn!', 'success');
    } catch (error) {
      console.error('Error withdrawing earnings:', error);
      showNotification('Failed to withdraw earnings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.referralsContainer}>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Processing...</p>
        </div>
      )}
      
      {notification.show && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.message}
        </div>
      )}

      <div className={styles.referralInfo}>
        <div className={styles.referralCard}>
          <h3>Your Referral Code</h3>
          <div className={styles.codeContainer}>
            <span className={styles.code}>{referralCode || 'No code generated'}</span>
            <button 
              className={styles.generateButton}
              onClick={generateReferralCode}
              disabled={isLoading}
            >
              Generate Code
            </button>
          </div>
          
          {referralCode && (
            <div className={styles.referralLinkContainer}>
              <h4>Share your referral link</h4>
              <div className={styles.linkDisplay}>
                <span className={styles.link}>{referralLink}</span>
                <button 
                  className={`${styles.copyButton} ${copySuccess ? styles.copied : ''}`}
                  onClick={() => copyToClipboard(referralLink)}
                  disabled={isLoading}
                >
                  {copySuccess ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className={styles.shareInfo}>
                Your friends will get a 5% bonus on their stakes, and you'll earn 5% of their rewards!
              </p>
            </div>
          )}
        </div>
        
        <div className={styles.earningsCard}>
          <h3>Your Referral Earnings</h3>
          <div className={styles.earningsDisplay}>
            <span className={styles.earningsValue}>{referralEarnings} ETH</span>
            <button 
              className={styles.withdrawButton}
              onClick={handleWithdrawReferralRewards}
              disabled={parseFloat(referralEarnings) <= 0 || isLoading}
            >
              Withdraw Earnings
            </button>
          </div>
        </div>
      </div>
      
      <div className={styles.referralsList}>
        <h3>Your Referrals</h3>
        
        {referrals.length === 0 ? (
          <div className={styles.noReferrals}>
            <p>You don't have any referrals yet. Share your code to start earning!</p>
          </div>
        ) : (
          <table className={styles.referralsTable}>
            <thead>
              <tr>
                <th>Address</th>
                <th>Staked Amount</th>
                <th>Your Earnings</th>
                <th>Date Joined</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((referral, index) => (
                <tr key={index}>
                  <td>
                    <span className={styles.address}>{referral.address}</span>
                  </td>
                  <td>{referral.stakedAmount} ETH</td>
                  <td>{referral.earnings} ETH</td>
                  <td>{referral.joinedDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      <div className={styles.referralsInfo}>
        <div className={styles.infoItem}>
          <span className={styles.infoIcon} role="img" aria-label="Information">ℹ️</span>
          <p>Earn ETH by referring friends to stake on the platform</p>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoIcon} role="img" aria-label="Gift">🎁</span>
          <p>Your friends get 5% bonus on their stakes</p>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoIcon} role="img" aria-label="Money">💰</span>
          <p>You earn 5% of your referrals' rewards</p>
        </div>
      </div>
    </div>
  );
};

export default Referrals; 